import { useEffect, useMemo, useRef, useState } from 'react';

// Local component acts as a placeholder/anchor only. The meeting instance is rendered globally.

import * as S from './styles';
import Spin from 'components/Spin';
import { useMeeting } from 'global/MeetingContext';
import { useUser } from 'global/UserContext';
import { useToast } from 'hooks/useToast';
import GlobalModal from 'modals/GlobalModal/GlobalModal';
import { MeetingRoom as IMeetingRoom } from 'pages/MeetingRoom/types/meetingTypes';
import { apiMeeting } from 'services/apiService';

interface JitsiEmbedProps {
  meetingRoom: IMeetingRoom;
  videoLoading: boolean;
  onLoad: () => void;
  collectionIndex?: number;
  enterRequestId?: number;
  onEnterMeeting?: () => void;
}

interface IJitsiAuth {
  app_id: string;
  token: string;
}
export const JitsiVideoEmbed = ({
  meetingRoom,
  videoLoading,
  onLoad,
  collectionIndex = 0,
  enterRequestId,
  onEnterMeeting,
}: JitsiEmbedProps) => {
  const { toastError } = useToast();
  const {
    registerInPageMeeting,
    unregisterInPageMeeting,
    attachToElement,
    detachAttachment,
    activeRoom,
    isActive,
    leaveMeeting,
  } = useMeeting();
  const [jitsiAuth, setJitsiAuth] = useState<IJitsiAuth>();
  const [error, setError] = useState<boolean>(false);
  const { user } = useUser();

  const [modalOpen, setModalOpen] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showSwitchMeetingModal, setShowSwitchMeetingModal] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const currentRoomId = useMemo(
    () =>
      String(
        (meetingRoom as any)?.room_data?.internal_id ?? (meetingRoom as any)?.room_data?.id ?? ''
      ),
    [meetingRoom]
  );
  const activeRoomId = useMemo(
    () => String(activeRoom?.room_data?.internal_id ?? activeRoom?.room_data?.id ?? ''),
    [activeRoom]
  );

  useEffect(() => {
    // Avoid requesting a new token if a meeting is already active for this room
    const currentId = String(
      (meetingRoom as any)?.room_data?.internal_id ?? (meetingRoom as any)?.room_data?.id ?? ''
    );
    const activeId = String(activeRoom?.room_data?.internal_id ?? activeRoom?.room_data?.id ?? '');
  }, []);

  const getToken = async () => {
    try {
      const response = await apiMeeting.get(`/v1/integrations/jitsi/token?user_id=${user?.id}`);
      setJitsiAuth(response?.data ?? '');
      onLoad();
    } catch (error) {
      setError(true);
      toastError('Algo deu errado ao carregar o vídeo');
      console.log('VIDEOCONF', { error });
    }
  };

  const handleEnterMeeting = () => {
    const isDifferentActive =
      isActive && !!activeRoomId && !!currentRoomId && activeRoomId !== currentRoomId;
    if (isDifferentActive) {
      setShowSwitchMeetingModal(true);
      return;
    }
    setShowMeetingModal(true);
  };

  useEffect(() => {
    if (enterRequestId) {
      handleEnterMeeting();
    }
  }, [enterRequestId]);

  const handleCloseMeetingModal = () => {
    setShowMeetingModal(false);
  };

  const handleConfirmEnterMeeting = () => {
    if (!(isActive && activeRoomId && activeRoomId === currentRoomId)) {
      getToken();
    }
    setShowMeetingModal(false);
    setModalOpen(true);
  };

  const handleConfirmSwitchMeeting = () => {
    setShowSwitchMeetingModal(false);
    try {
      leaveMeeting();
    } finally {
      setTimeout(() => handleConfirmEnterMeeting(), 100);
    }
  };

  // Quando mudar de rota (história), ativa automaticamente PiP se reunião estiver aberta
  useEffect(() => {
    if (modalOpen && jitsiAuth) {
      registerInPageMeeting(
        {
          room_data: {
            title: meetingRoom.room_data.title,
            id: meetingRoom.room_data.id,
            internal_id: meetingRoom.room_data.internal_id ?? '',
            key: meetingRoom.room_data?.key ?? '',
            organization_uuid: meetingRoom.room_data?.organization_uuid ?? '',
            event_id: meetingRoom.room_data?.event_id ?? '',
          },
        },
        jitsiAuth,
        () => {
          // When PiP (global) is ready, attach it to our inline container to avoid disconnects
          if (containerRef.current) {
            attachToElement(containerRef.current);
          }
        }
      );
    } else {
      detachAttachment();
      unregisterInPageMeeting();
    }
  }, [
    modalOpen,
    jitsiAuth,
    meetingRoom.room_data.title,
    meetingRoom.room_data.id,
    registerInPageMeeting,
    unregisterInPageMeeting,
  ]);

  // If user returns to the original room page while a meeting is active, auto-attach overlay
  useEffect(() => {
    if (
      isActive &&
      activeRoomId &&
      currentRoomId &&
      activeRoomId === currentRoomId &&
      containerRef.current
    ) {
      attachToElement(containerRef.current);
    }
  }, [isActive, activeRoomId, currentRoomId, attachToElement]);

  // When meeting ends (user clicked hangup inside Jitsi), close local UI and cleanup
  useEffect(() => {
    if (!isActive) {
      setModalOpen(false);
      setShowMeetingModal(false);
      detachAttachment();
      unregisterInPageMeeting();
    }
  }, [isActive, detachAttachment, unregisterInPageMeeting]);

  // No local renderJitsi. The global provider renders and attaches to our container.

  return (
    <>
      <GlobalModal
        open={showSwitchMeetingModal}
        title="Trocar de reunião"
        description={`Você já está em uma reunião. Deseja sair e entrar em "${meetingRoom.room_data.title}"?`}
        onClose={() => setShowSwitchMeetingModal(false)}
        onCancel={() => setShowSwitchMeetingModal(false)}
        onConfirm={handleConfirmSwitchMeeting}
        confirmButtonText="Trocar"
        cancelButtonText="Cancelar"
        closeOnBackdropClick={false}
        width="400px"
        height="auto"
      />

      <GlobalModal
        open={showMeetingModal}
        title="Entrar na Reunião"
        description={`Você está prestes a entrar na sala "${meetingRoom.room_data.title}". Deseja continuar?`}
        onClose={handleCloseMeetingModal}
        onCancel={handleCloseMeetingModal}
        onConfirm={handleConfirmEnterMeeting}
        confirmButtonText="Entrar"
        cancelButtonText="Cancelar"
        closeOnBackdropClick={false}
        width="400px"
        height="auto"
      />

      <S.VideoContainer className="jitsi-video-container" ref={containerRef}>
        {modalOpen && (!isActive || !activeRoom) && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Spin tip="Loading video..." />
          </div>
        )}
      </S.VideoContainer>

      {error && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <p>Erro ao carregar o vídeo</p>
        </div>
      )}
    </>
  );
};
